import { memo } from 'react';

// 38 dense base pair rungs precisely mapped along the sweeping diagonal ribbon trajectory
const RUNG_DATA = [
  { x: -40, y1: 950, y2: 780 },
  { x: -10, y1: 950, y2: 784 },
  { x: 20, y1: 945, y2: 790 },
  { x: 50, y1: 935, y2: 798 },
  { x: 80, y1: 918, y2: 810 },
  { x: 110, y1: 895, y2: 825 },
  { x: 140, y1: 865, y2: 842 },
  { x: 170, y1: 832, y2: 858 },
  { x: 200, y1: 795, y2: 870 },
  { x: 230, y1: 758, y2: 876 },
  { x: 260, y1: 720, y2: 875 },
  { x: 290, y1: 686, y2: 868 },
  { x: 320, y1: 660, y2: 855 },
  { x: 350, y1: 645, y2: 838 },
  { x: 380, y1: 642, y2: 820 },
  { x: 410, y1: 650, y2: 805 },
  { x: 440, y1: 664, y2: 795 },
  { x: 470, y1: 678, y2: 790 },
  { x: 500, y1: 692, y2: 788 },
  { x: 530, y1: 704, y2: 790 },
  { x: 560, y1: 712, y2: 798 },
  { x: 590, y1: 712, y2: 810 },
  { x: 620, y1: 702, y2: 828 },
  { x: 650, y1: 680, y2: 850 },
  { x: 680, y1: 645, y2: 874 },
  { x: 710, y1: 598, y2: 896 },
  { x: 740, y1: 545, y2: 910 },
  { x: 770, y1: 490, y2: 914 },
  { x: 800, y1: 442, y2: 905 },
  { x: 830, y1: 408, y2: 885 },
  { x: 860, y1: 390, y2: 850 },
  { x: 890, y1: 388, y2: 800 },
  { x: 920, y1: 398, y2: 740 },
  { x: 950, y1: 418, y2: 670 },
  { x: 980, y1: 440, y2: 590 },
  { x: 1010, y1: 452, y2: 510 },
  { x: 1040, y1: 448, y2: 440 },
  { x: 1070, y1: 420, y2: 370 },
];

// Ambient floating particles
const PARTICLES = Array.from({ length: 22 }).map((_, i) => ({
  cx: Math.floor(Math.sin(i * 345) * 600 + 400),
  r: (i % 3) + 1.5,
  delay: -(i * 1.3),
}));

export const DnaAnimation = memo(function DnaAnimation() {
  return (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none flex items-center justify-center opacity-90 select-none">
      <svg
        viewBox="0 0 1000 1000"
        preserveAspectRatio="xMidYMid slice"
        className="h-full w-full"
      >
        <defs>
          {/* Background Dot Matrix Pattern */}
          <pattern id="dot-grid" width="32" height="32" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.2" fill="rgba(255, 255, 255, 0.07)" />
          </pattern>

          {/* Grid Lines Pattern */}
          <pattern id="tech-grid" width="80" height="80" patternUnits="userSpaceOnUse">
            <path d="M 80 0 L 0 0 0 80" fill="none" stroke="rgba(255, 255, 255, 0.03)" strokeWidth="1" />
          </pattern>

          {/* Ribbon Gradients */}
          <linearGradient id="dna-ribbon-teal" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0d9488" stopOpacity="0.2" />
            <stop offset="35%" stopColor="#2dd4bf" stopOpacity="0.95" />
            <stop offset="65%" stopColor="#14b8a6" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#0d9488" stopOpacity="0.2" />
          </linearGradient>

          <linearGradient id="dna-ribbon-violet" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6d28d9" stopOpacity="0.2" />
            <stop offset="35%" stopColor="#a78bfa" stopOpacity="0.95" />
            <stop offset="65%" stopColor="#818cf8" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#6d28d9" stopOpacity="0.2" />
          </linearGradient>

          <linearGradient id="glow-cyan" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#67e8f9" stopOpacity="0" />
            <stop offset="50%" stopColor="#67e8f9" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#67e8f9" stopOpacity="0" />
          </linearGradient>

          {/* Glow Filters */}
          <filter id="ribbon-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="ribbon-shadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="2" dy="5" stdDeviation="4" floodColor="#020617" floodOpacity="0.6" />
          </filter>

          <filter id="soft-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <style>
          {`
            .flow-ribbon-1 {
              stroke-dasharray: 2600;
              stroke-dashoffset: 2600;
              animation: flow-anim 14s infinite linear;
            }
            .flow-ribbon-2 {
              stroke-dasharray: 2600;
              stroke-dashoffset: -2600;
              animation: flow-anim-reverse 16s infinite linear;
            }
            .pulse-slow {
              animation: pulse-glow 6s ease-in-out infinite alternate;
            }
            .float-particle {
              animation: float-up 18s linear infinite;
              opacity: 0;
            }
            @keyframes flow-anim {
              0% { stroke-dashoffset: 2600; }
              100% { stroke-dashoffset: -2600; }
            }
            @keyframes flow-anim-reverse {
              0% { stroke-dashoffset: -2600; }
              100% { stroke-dashoffset: 2600; }
            }
            @keyframes pulse-glow {
              0% { opacity: 0.15; transform: scale(0.95); }
              100% { opacity: 0.35; transform: scale(1.05); }
            }
            @keyframes float-up {
              0% { transform: translateY(1050px); opacity: 0; }
              15% { opacity: 0.55; }
              85% { opacity: 0.55; }
              100% { transform: translateY(-50px); opacity: 0; }
            }
          `}
        </style>

        {/* 1. Background Grid & Dot Matrix Patterns */}
        <rect width="1000" height="1000" fill="url(#dot-grid)" />
        <rect width="1000" height="1000" fill="url(#tech-grid)" />

        {/* 2. Ambient Accent Rings & Decorative Elements */}
        <g className="pulse-slow" transform-origin="850 180">
          <circle cx="850" cy="180" r="220" fill="none" stroke="rgba(45, 212, 191, 0.12)" strokeWidth="1" strokeDasharray="8 8" />
          <circle cx="850" cy="180" r="140" fill="none" stroke="rgba(167, 139, 250, 0.15)" strokeWidth="1.5" />
        </g>

        <g opacity="0.25">
          <path d="M 120 160 H 140 M 130 150 V 170" stroke="#2dd4bf" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 780 720 H 800 M 790 710 V 730" stroke="#a78bfa" strokeWidth="2.5" strokeLinecap="round" />
        </g>

        {/* 3. The Diagonal Ribbon Sweep with 38 Dense Base Pair Rungs */}
        <g transform="translate(0, 0)">
          {/* Dense Base Pair Rungs along the entire diagonal sweep */}
          <g filter="url(#soft-glow)">
            {RUNG_DATA.map((rung, i) => {
              const rungLength = Math.abs(rung.y1 - rung.y2);
              const opacity = Math.min(1, Math.max(0.2, rungLength / 120)).toFixed(2);

              return (
                <g key={`rung-${i}`} opacity={opacity}>
                  {/* Connecting base pair rung line */}
                  <line
                    x1={rung.x}
                    y1={rung.y1}
                    x2={rung.x}
                    y2={rung.y2}
                    stroke="rgba(255, 255, 255, 0.4)"
                    strokeWidth="2"
                    strokeDasharray={i % 3 === 0 ? 'none' : '4 3'}
                  />

                  {/* Left Node Cap (Teal) */}
                  <circle cx={rung.x} cy={rung.y1} r="4" fill="#2dd4bf" />

                  {/* Right Node Cap (Violet) */}
                  <circle cx={rung.x} cy={rung.y2} r="4" fill="#a78bfa" />
                </g>
              );
            })}
          </g>

          {/* BACK STRAND: Ribbon Strand 2 (Violet Ribbon) */}
          <path
            d="M -150 780 C 150 780, 250 880, 450 820 C 650 760, 750 920, 950 870 C 1150 820, 1150 480, 1250 480"
            fill="none"
            stroke="url(#dna-ribbon-violet)"
            strokeWidth="8"
            strokeLinecap="round"
            filter="url(#ribbon-glow)"
            className="flow-ribbon-2"
          />

          {/* Parallel Accent Line for Ribbon Thickness */}
          <path
            d="M -150 784 C 150 784, 250 884, 450 824 C 650 764, 750 924, 950 874 C 1150 824, 1150 484, 1250 484"
            fill="none"
            stroke="rgba(255,255,255,0.25)"
            strokeWidth="1.5"
            strokeLinecap="round"
            className="flow-ribbon-2"
          />

          {/* FRONT STRAND: Ribbon Strand 1 (Teal Ribbon with Drop Shadow) */}
          <path
            d="M -150 950 C 150 950, 250 620, 450 670 C 650 720, 750 380, 950 430 C 1150 480, 1150 150, 1250 150"
            fill="none"
            stroke="url(#dna-ribbon-teal)"
            strokeWidth="8"
            strokeLinecap="round"
            filter="url(#ribbon-shadow)"
            className="flow-ribbon-1"
          />

          {/* Parallel Accent Line for Ribbon Thickness */}
          <path
            d="M -150 954 C 150 954, 250 624, 450 674 C 650 724, 750 384, 950 434 C 1150 484, 1150 154, 1250 154"
            fill="none"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="1.5"
            strokeLinecap="round"
            className="flow-ribbon-1"
          />

          {/* Glowing Shimmer Pulse Beam along Front Ribbon */}
          <path
            d="M -150 950 C 150 950, 250 620, 450 670 C 650 720, 750 380, 950 430 C 1150 480, 1150 150, 1250 150"
            fill="none"
            stroke="url(#glow-cyan)"
            strokeWidth="3"
            strokeLinecap="round"
            filter="url(#soft-glow)"
            className="flow-ribbon-1"
            style={{ animationDuration: '8s' }}
          />
        </g>

        {/* 4. Ambient Floating Data Particles */}
        {PARTICLES.map((particle, i) => (
          <circle
            key={`particle-${i}`}
            cx={particle.cx}
            cy="0"
            r={particle.r}
            fill={i % 2 === 0 ? '#2dd4bf' : '#a78bfa'}
            className="float-particle"
            style={{ animationDelay: `${particle.delay}s` }}
            filter="url(#soft-glow)"
          />
        ))}
      </svg>
    </div>
  );
});
